import { useEffect, useState } from "react";
import { Power } from "lucide-react";
import RiderWorkSummary from "../components/RiderWorkSummary";
import RiderShiftNotice from "../components/RiderShiftNotice";
import RiderWithdrawalNotice from "../components/RiderWithdrawalNotice";
import RiderWorkHoldNotice from "../components/RiderWorkHoldNotice";

import { useRiderPortal } from "../context/RiderPortalContext";
import { riderApi } from "../services/riderApi";

const DUTY_OPTIONS = [
  { value: "ON_DUTY", label: "Available" },
  { value: "PAUSED", label: "Pause new work" },
  { value: "OFF_DUTY", label: "Off duty" },
];

// "1 parcel and ₱509.99 in cash", said the way a Rider would.
function describeHeld({ parcelCount = 0, cashAmountCents = 0 } = {}) {
  const parts = [];
  if (parcelCount) parts.push(`${parcelCount} ${parcelCount === 1 ? "parcel" : "parcels"}`);
  if (cashAmountCents) parts.push(`₱${(cashAmountCents / 100).toLocaleString("en-PH", { minimumFractionDigits: 2 })} in cash`);
  return parts.join(" and ") || "delivery items";
}

function getDutyUpdateErrorMessage(error) {
  if (error?.code === "RIDER_ACTIVE_CUSTODY") {
    return "You can't go off duty while you still have an active delivery.";
  }
  if (error?.code === "DELIVERY_PHYSICAL_OBLIGATIONS_OPEN") {
    return "Hand over your parcels and cash at the store first. If the store has already closed for your trip, you can go off duty and keep them until it opens.";
  }
  if (error?.code === "RIDER_DUTY_SUSPENDED") {
    return "Duty changes aren't available right now. Contact your store manager if you need help.";
  }
  if (error?.code === "RIDER_DUTY_VERSION_CONFLICT") {
    return "Your duty status changed elsewhere. We refreshed it; please try again.";
  }
  return "We couldn't update your duty status. Please try again.";
}

export default function RiderHome() {
  const { businessId, context, refreshContext, stale = false, refreshing = false } = useRiderPortal();
  const [duty, setDuty] = useState(context?.duty || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [workHolds, setWorkHolds] = useState(null);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    setDuty(context?.duty || null);
  }, [context]);

  // A cash hold is opened when a trip ends, so the Rider is never on the run
  // screen when it happens - they come here, see no new work, and would otherwise
  // have no way to learn why. Read-only, and silent when nothing is held.
  useEffect(() => {
    if (!businessId) return undefined;
    const controller = new AbortController();
    riderApi.getMyWorkHolds(businessId, { signal: controller.signal })
      .then((held) => setWorkHolds(held))
      .catch(() => { /* the duty screen still works without this */ });
    return () => controller.abort();
  }, [businessId]);

  const updateDuty = async (dutyState) => {
    if (!duty || stale || refreshing || saving || duty.dutyState === dutyState) return;
    setSaving(true);
    setError("");
    try {
      const updated = await riderApi.updateDuty(businessId, {
        dutyState,
        expectedVersion: duty.version,
      });
      setDuty(updated);
      setNotice(updated?.custodyKeptOvernight
        ? `You're off duty and still responsible for ${describeHeld(updated.custodyKeptOvernight)}. Hand them over at the store when it opens; new trips resume after that.`
        : "");
      await refreshContext();
    } catch (requestError) {
      setError(getDutyUpdateErrorMessage(requestError));
      if (requestError?.code === "RIDER_DUTY_VERSION_CONFLICT") {
        await refreshContext();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <section>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300">Today</p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">Your delivery work</h1>
        <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">Set your availability for deliveries from this store.</p>
      </section>

      {notice ? <p role="status" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-100">{notice}</p> : null}

      {duty?.overnightCustody && !notice ? (
        <section className="rounded-3xl border border-amber-200 bg-amber-50 p-5 text-amber-950 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-100" data-testid="overnight-custody">
          <h2 className="font-semibold">You still have {describeHeld(duty.overnightCustody)} from your last trip</h2>
          <p className="mt-1 text-sm">The store closed before they were handed over, so they stay in your care. Bring them to the store counter when it opens. You won't get new trips until staff take them.</p>
        </section>
      ) : null}

      <RiderWorkHoldNotice holds={workHolds} />

      <RiderWithdrawalNotice withdrawals={duty?.recentWithdrawals} />

      <RiderShiftNotice duty={duty} />

      <RiderWorkSummary duty={duty} stale={stale} />

      <section className="rounded-3xl border border-stone-200 bg-white p-5 text-stone-950 shadow-sm dark:border-white/10 dark:bg-neutral-900 dark:text-white">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm opacity-70">Delivery availability</p>
            <p className="mt-1 text-2xl font-semibold">{({ ON_DUTY: "Available for deliveries", PAUSED: "New work paused", OFF_DUTY: "Off duty", SUSPENDED: "Availability suspended" })[duty?.dutyState] || "Unavailable"}</p>
          </div>
          <Power className="h-7 w-7 text-emerald-700 dark:text-emerald-300" />
        </div>
        <div className="mt-5 grid grid-cols-3 gap-2">
          {DUTY_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => updateDuty(option.value)}
              disabled={saving || stale || refreshing || duty?.dutyState === "SUSPENDED"}
              aria-pressed={duty?.dutyState === option.value}
              className={`min-h-12 rounded-2xl px-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 disabled:opacity-60 ${duty?.dutyState === option.value ? "bg-emerald-700 text-white" : "bg-stone-100 text-stone-900 dark:bg-white/10 dark:text-white"}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </section>

      {error && <p role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">{error}</p>}

      <p className="text-sm leading-6 text-stone-600 dark:text-stone-400">Available: receive committed delivery work. Pause new work: stop new commitments and pickup; keep responsibility for parcels already held. Off duty: finish after custody is cleared. Availability does not clock attendance or change your schedule.</p>
    </div>
  );
}
